/**
 * AuditArchivesPage — viewer for the JSONL archive files written by the
 * housekeeping audit-archival tick.
 *
 * Two-pane layout:
 *   - Left rail: list of archive files (newest first), each clickable.
 *   - Right pane: events from the selected file, paginated.
 *
 * Deliberately simpler than the live /audit page — the live page has
 * server-side filters (kind/vendor/q); this page only supports per-file
 * viewing. Operators querying for a specific event in a 6-month-old
 * archive can reach for grep on the JSONL directly; this surface is
 * for spot-checks + compliance attestation.
 */
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Archive,
  ChevronLeft,
  ChevronRight,
  FileText,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { PanelSkeleton } from "@/components/ui/skeleton";
import { get as apiGet } from "@/api/client";
import { cn } from "@/lib/cn";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

interface ArchiveFile {
  name: string;
  sizeBytes: number;
  mtime: number;
  lineCount: number;
}

interface ArchivedEvent {
  id?: string;
  occurredAt?: string;
  kind?: string;
  username?: string;
  vendor?: string;
  summary?: string;
  detail?: unknown;
  remoteIp?: string;
  modelName?: string;
  recordId?: string;
}

const PAGE_SIZE = 200;

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatMtime(epochSec: number): string {
  return new Date(epochSec * 1000).toLocaleString();
}

export function AuditArchivesPage() {
  const animatedRootRef = useUiAutoAnimate();

  type FilesState = { files: ArchiveFile[]; filesLoading: boolean; filesError: string | null };
  const [{ files, filesLoading, filesError }, setFilesState] = useReducer(
    (s: FilesState, p: Partial<FilesState>) => ({ ...s, ...p }),
    { files: [], filesLoading: true, filesError: null },
  );

  const [selected, setSelected] = useState<string | null>(null);
  // Ref mirror so loadFiles can check the current selection without being
  // listed as a reactive dep (which would cause the mount effect to re-run).
  const selectedRef = useRef(selected);
  useEffect(() => {
    selectedRef.current = selected;
  });

  type EventsState = { events: ArchivedEvent[]; total: number; offset: number; eventsLoading: boolean; eventsError: string | null };
  const [{ events, total, offset, eventsLoading, eventsError }, setEventsState] = useReducer(
    (s: EventsState, p: Partial<EventsState>) => ({ ...s, ...p }),
    { events: [], total: 0, offset: 0, eventsLoading: false, eventsError: null },
  );

  const loadEvents = useCallback(async (name: string, off: number) => {
    setEventsState({ eventsLoading: true, eventsError: null });
    try {
      const raw = await apiGet<{
        name: string;
        total: number;
        offset: number;
        limit: number;
        events: ArchivedEvent[];
      }>(`/audit/archives/${encodeURIComponent(name)}`, {
        limit: PAGE_SIZE,
        offset: off,
      });
      setEventsState({ events: raw.events, total: raw.total, offset: raw.offset });
    } catch (err) {
      setEventsState({ eventsError: err instanceof Error ? err.message : "unknown error", events: [] });
    } finally {
      setEventsState({ eventsLoading: false });
    }
  }, []); // setEventsState is stable (useReducer dispatch)

  const loadFiles = useCallback(
    () =>
      apiGet<ArchiveFile[]>("/audit/archives/")
        .then((raw) => {
          setFilesState({ files: raw, filesLoading: false });
          // Auto-select the newest file on first load — common case.
          if (raw.length > 0 && !selectedRef.current) {
            setSelected(raw[0].name);
            void loadEvents(raw[0].name, 0);
          }
        })
        .catch((err) => {
          setFilesState({
            filesError: err instanceof Error ? err.message : "unknown error",
            filesLoading: false,
          });
        }),
    [loadEvents],
  );

  useEffect(() => {
    void loadFiles();
  }, [loadFiles]);

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <Link to="/audit" className="text-fg-dim hover:text-fg">
          Audit log
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Archives</span>
      </div>

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            Audit log archives
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Cold-tail audit events the housekeeping tick has moved out of the
            live table. One JSONL file per archive run; click a file to load its
            events.
          </p>
        </div>
        <Badge variant="info" size="md">
          <Archive size={11} />
          {files.length} archive{files.length === 1 ? "" : "s"}
        </Badge>
      </div>

      {filesError ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-rose/30 bg-rose/10 px-3.5 py-2.5 text-sm text-rose"
        >
          Could not load archives — {filesError}
        </output>
      ) : null}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[280px_1fr]">
        {/* Left rail — file list */}
        <Card>
          <CardContent className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
              <CardTitle>Files</CardTitle>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => void loadFiles()}
                disabled={filesLoading}
              >
                <RefreshCw
                  size={11}
                  className={filesLoading ? "animate-spin" : ""}
                />
              </Button>
            </div>
            {files.length === 0 ? (
              filesLoading ? (
                <PanelSkeleton />
              ) : (
                <CardSub>
                  No archives yet — first archival run produces the first file.
                </CardSub>
              )
            ) : (
              <ul className="m-0 flex max-h-[640px] list-none flex-col gap-1 overflow-y-auto p-0">
                {files.map((f) => (
                  <li key={f.name}>
                    <button
                      type="button"
                      onClick={() => { setSelected(f.name); void loadEvents(f.name, 0); }}
                      className={cn(
                        "flex w-full flex-col gap-0.5 rounded-md border px-2.5 py-2 text-left transition-colors",
                        selected === f.name
                          ? "border-violet/40 bg-aurora-soft"
                          : "border-line bg-surface hover:bg-surface-2",
                      )}
                    >
                      <div className="flex items-center gap-1.5">
                        <FileText size={11} className="shrink-0 text-violet" />
                        <span className="truncate font-mono text-[11px] text-fg">
                          {f.name}
                        </span>
                      </div>
                      <div className="font-mono text-[10px] text-fg-mute">
                        {f.lineCount.toLocaleString()} events ·{" "}
                        {formatBytes(f.sizeBytes)} · {formatMtime(f.mtime)}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Right pane — events from selected file */}
        <Card>
          <CardContent className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-base">
                {selected ?? (
                  <span className="text-fg-mute">No file selected</span>
                )}
              </CardTitle>
              {selected && total > 0 ? (
                <CardSub>
                  rows {offset + 1}–{Math.min(offset + events.length, total)} of{" "}
                  {total.toLocaleString()}
                </CardSub>
              ) : null}
              <div className="ml-auto flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    selected &&
                    void loadEvents(selected, Math.max(0, offset - PAGE_SIZE))
                  }
                  disabled={!selected || offset === 0 || eventsLoading}
                >
                  <ChevronLeft size={11} />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    selected && void loadEvents(selected, offset + PAGE_SIZE)
                  }
                  disabled={
                    !selected ||
                    offset + events.length >= total ||
                    eventsLoading
                  }
                >
                  <ChevronRight size={11} />
                </Button>
              </div>
            </div>
            {eventsError ? (
              <div className="rounded-md border border-rose/30 bg-rose/10 px-3 py-2 text-sm text-rose">
                {eventsError}
              </div>
            ) : null}
            {!selected ? (
              <CardSub>Pick a file from the left to load its events.</CardSub>
            ) : eventsLoading && events.length === 0 ? (
              <PanelSkeleton />
            ) : events.length === 0 ? (
              <CardSub>(empty file)</CardSub>
            ) : (
              <div className="flex flex-col gap-1">
                {events.map((e) => (
                  <div
                    key={e.id ?? `${e.occurredAt ?? ""}|${e.kind ?? ""}|${e.recordId ?? e.summary ?? ""}`}
                    className="grid grid-cols-[auto_auto_auto_1fr_auto] items-center gap-2 rounded-md border border-line bg-surface px-2.5 py-1.5 text-[11.5px]"
                  >
                    <span className="font-mono text-[10.5px] text-fg-mute">
                      {e.occurredAt ?? "—"}
                    </span>
                    <Badge variant="neutral" size="sm">
                      {e.kind ?? "?"}
                    </Badge>
                    <Badge variant="neutral" size="sm">
                      {e.vendor ?? "—"}
                    </Badge>
                    <span className="truncate text-fg" title={e.summary}>
                      {e.summary ?? ""}
                    </span>
                    <span className="font-mono text-[10px] text-fg-mute">
                      {e.username ?? "—"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
